/**
 * Small charts for the overview (D-39). Plain HTML and CSS on the HeroUI theme variables, so they
 * follow light and dark mode. HeroUI declares its Tailwind colours with `@theme inline`, so runtime
 * styles must use the base variables (`--accent`, `--surface`), not `--color-*`.
 *
 * Rules (dataviz guidance): magnitude uses one hue from light to dark, touching marks are separated by a
 * 2px gap, text never wears the data colour, and every mark has a tooltip on hover and keyboard focus.
 */
import { type CSSProperties, type KeyboardEvent, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export type Hue = "accent" | "danger";

/** Five sequential steps of one hue, mixed into the surface so they work in both themes. */
const STEPS = [18, 34, 54, 76, 100];
const STEP_IDS = STEPS.map((_, s) => s);
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const EMPTY = "var(--surface-secondary)";

function stepColor(hue: Hue, step: number) {
  return `color-mix(in oklab, var(--${hue}) ${STEPS[step]}%, var(--surface))`;
}

function stepOf(value: number, max: number) {
  if (value <= 0 || max <= 0) return -1;
  return Math.min(STEPS.length - 1, Math.floor((value / max) * STEPS.length - 1e-9));
}

function useLocale() {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage === "id" ? "id-ID" : "en-US";
}

function useNumber() {
  const locale = useLocale();
  return useMemo(() => new Intl.NumberFormat(locale), [locale]);
}

/** Weekday names, Monday first. 2024-01-01 was a Monday. */
export function useWeekdays(style: "short" | "long" = "short") {
  const locale = useLocale();
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: style, timeZone: "UTC" });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
  }, [locale, style]);
}

const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;

/** Arrow-key navigation with a single tab stop over a grid of `rows x cols` items. */
function useRoving(rows: number, cols: number) {
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState<number | null>(null);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const r = Math.floor(active / cols);
    const c = active % cols;
    let next = active;
    if (e.key === "ArrowRight") next = r * cols + Math.min(cols - 1, c + 1);
    else if (e.key === "ArrowLeft") next = r * cols + Math.max(0, c - 1);
    else if (e.key === "ArrowDown") next = Math.min(rows - 1, r + 1) * cols + c;
    else if (e.key === "ArrowUp") next = Math.max(0, r - 1) * cols + c;
    else if (e.key === "Home") next = r * cols;
    else if (e.key === "End") next = r * cols + cols - 1;
    else return;
    e.preventDefault();
    setActive(next);
    setFocused(next);
    const container = e.currentTarget.closest("[data-roving]");
    (container?.querySelector(`[data-index="${next}"]`) as HTMLElement | null)?.focus();
  };
  return { active, setActive, focused, setFocused, onKeyDown };
}

/**
 * Tooltip above a point. `x` is the horizontal position from 0 to 1; near the edges the box is
 * anchored to that side so it never leaves the card.
 */
function Tooltip({ x, top, children }: { x: number; top: string; children: ReactNode }) {
  const shift = x < 0.2 ? "0%" : x > 0.8 ? "-100%" : "-50%";
  const style: CSSProperties = {
    left: `${x * 100}%`,
    top,
    transform: `translate(${shift}, calc(-100% - 6px))`,
  };
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-20 flex flex-col gap-1 rounded-lg bg-overlay px-3 py-2 text-xs whitespace-nowrap text-overlay-foreground shadow-lg"
      style={style}
    >
      {children}
    </div>
  );
}

/** Tooltip row: the value leads, the label follows, the key is a short stroke of the mark colour. */
/** Tooltip row: label on the left, the value strong on the right, so no plural forms are needed. */
function TipRow({ value, label, color }: { value: string; label: string; color?: string }) {
  return (
    <span className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-2 opacity-70">
        {color ? <span className="h-0.5 w-3 rounded-full" style={{ background: color }} /> : null}
        {label}
      </span>
      <span className="font-semibold tabular-nums">{value}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Heatmap: weekday x hour
// ---------------------------------------------------------------------------

export function Heatmap({
  values,
  problems,
  hue,
  metric,
}: {
  /** 7 x 24 deliveries, Monday first. */
  values: number[][];
  problems: number[][];
  hue: Hue;
  metric: "volume" | "problems";
}) {
  const { t } = useTranslation();
  const number = useNumber();
  const days = useWeekdays("short");
  const longDays = useWeekdays("long");
  const shown = metric === "volume" ? values : problems;
  const max = Math.max(0, ...shown.flat());
  const roving = useRoving(7, 24);
  const [hover, setHover] = useState<number | null>(null);
  const current = hover ?? roving.focused;

  const cellLabel = (i: number) => {
    const d = Math.floor(i / 24);
    const h = i % 24;
    return t("overview.heatmap.cell", {
      day: longDays[d],
      hour: hourLabel(h),
      total: values[d]?.[h] ?? 0,
      problems: problems[d]?.[h] ?? 0,
    });
  };

  // Busiest slot, as a text summary that does not depend on reading colours.
  let busiest = -1;
  let busiestValue = 0;
  for (let d = 0; d < shown.length; d++) {
    for (let h = 0; h < 24; h++) {
      const v = shown[d]?.[h] ?? 0;
      if (v > busiestValue) {
        busiestValue = v;
        busiest = d * 24 + h;
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="-mx-1 overflow-x-auto px-1 pt-1 pb-2">
        <div className="grid min-w-[520px] grid-cols-[2.75rem_minmax(0,1fr)] gap-x-1">
          <div />
          <div className="grid grid-cols-24 gap-[2px] pb-1" aria-hidden>
            {HOURS.map((h) => (
              <div key={h} className="text-center text-[10px] text-muted tabular-nums">
                {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
              </div>
            ))}
          </div>
          <div className="grid grid-rows-7 gap-[2px] text-xs text-muted" aria-hidden>
            {days.map((day) => (
              <div key={day} className="flex items-center">
                {day}
              </div>
            ))}
          </div>
          <div className="relative grid grid-cols-24 gap-[2px]" data-roving>
            {days.map((day, d) =>
              HOURS.map((h) => {
                const i = d * 24 + h;
                const step = stepOf(shown[d]?.[h] ?? 0, max);
                return (
                  <button
                    type="button"
                    key={`${day}-${h}`}
                    data-index={i}
                    tabIndex={roving.active === i ? 0 : -1}
                    aria-label={cellLabel(i)}
                    onPointerEnter={() => setHover(i)}
                    onPointerLeave={() => setHover(null)}
                    onFocus={() => {
                      roving.setActive(i);
                      roving.setFocused(i);
                    }}
                    onBlur={() => roving.setFocused(null)}
                    onKeyDown={roving.onKeyDown}
                    className="aspect-square min-h-3 cursor-default rounded-[3px] outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-focus"
                    style={{ background: step < 0 ? EMPTY : stepColor(hue, step) }}
                  />
                );
              }),
            )}
            {current !== null ? (
              <Tooltip x={((current % 24) + 0.5) / 24} top={`${(Math.floor(current / 24) / 7) * 100}%`}>
                <span className="font-medium">
                  {longDays[Math.floor(current / 24)]}, {hourLabel(current % 24)}
                </span>
                <TipRow
                  value={number.format(values[Math.floor(current / 24)]?.[current % 24] ?? 0)}
                  label={t("overview.kpi.deliveries")}
                />
                <TipRow
                  value={number.format(problems[Math.floor(current / 24)]?.[current % 24] ?? 0)}
                  label={t("overview.kpi.problems")}
                />
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
        <span>
          {busiest >= 0
            ? t("overview.heatmap.busiest", {
                when: `${longDays[Math.floor(busiest / 24)]} ${hourLabel(busiest % 24)}`,
              })
            : t("overview.heatmap.empty")}
        </span>
        <div className="flex items-center gap-1.5" aria-hidden>
          <span className="mr-0.5">{t("overview.heatmap.less")}</span>
          <span className="size-3 rounded-[3px]" style={{ background: EMPTY }} />
          {STEP_IDS.map((s) => (
            <span key={s} className="size-3 rounded-[3px]" style={{ background: stepColor(hue, s) }} />
          ))}
          <span className="ml-0.5">{t("overview.heatmap.more")}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily columns: reached + not received, stacked
// ---------------------------------------------------------------------------

function niceMax(value: number) {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * magnitude >= value) return m * magnitude;
  }
  return value;
}

export function DailyBars({ days }: { days: Array<{ date: string; total: number; problems: number }> }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const number = useNumber();
  const fmtDay = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }),
    [locale],
  );
  const fmtLong = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }),
    [locale],
  );
  const compact = useMemo(() => new Intl.NumberFormat(locale, { notation: "compact" }), [locale]);
  const max = niceMax(Math.max(0, ...days.map((d) => d.total)));
  const roving = useRoving(1, days.length);
  const [hover, setHover] = useState<number | null>(null);
  const current = hover ?? roving.focused;
  const labelEvery = Math.max(1, Math.ceil(days.length / 7));
  const asDate = (d: string) => new Date(`${d}T00:00:00Z`);
  const describe = (i: number) => {
    const d = days[i]!;
    return t("overview.daily.tooltip", {
      date: fmtDay.format(asDate(d.date)),
      total: d.total,
      problems: d.problems,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm" style={{ background: "var(--accent)" }} />
          {t("overview.daily.reached")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm" style={{ background: "var(--danger)" }} />
          {t("overview.daily.problems")}
        </span>
      </div>
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-2">
        <div className="relative h-44 text-right text-[10px] text-muted tabular-nums" aria-hidden>
          {[1, 0.5, 0].map((f) => (
            <span key={f} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - f) * 100}%` }}>
              {compact.format(max * f)}
            </span>
          ))}
        </div>
        <div className="relative h-44" data-roving>
          {[0, 0.5, 1].map((f) => (
            <div
              key={f}
              className="absolute inset-x-0 border-t border-separator"
              style={{ top: `${f * 100}%` }}
            />
          ))}
          <div className="absolute inset-0 flex items-end justify-around gap-[2px]">
            {days.map((d, i) => {
              const reached = Math.max(0, d.total - d.problems);
              return (
                <div
                  key={d.date}
                  data-index={i}
                  tabIndex={roving.active === i ? 0 : -1}
                  role="img"
                  aria-label={describe(i)}
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => {
                    roving.setActive(i);
                    roving.setFocused(i);
                  }}
                  onBlur={() => roving.setFocused(null)}
                  onKeyDown={roving.onKeyDown}
                  className="group flex h-full min-w-0 flex-1 justify-center outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <div className="flex h-full w-full max-w-6 flex-col justify-end gap-[2px]">
                    {d.problems > 0 ? (
                      <div
                        className="w-full rounded-t-[4px] transition-opacity group-hover:opacity-80"
                        style={{ height: `${(d.problems / max) * 100}%`, background: "var(--danger)" }}
                      />
                    ) : null}
                    {reached > 0 ? (
                      <div
                        className={`w-full transition-opacity group-hover:opacity-80 ${d.problems > 0 ? "" : "rounded-t-[4px]"}`}
                        style={{ height: `${(reached / max) * 100}%`, background: "var(--accent)" }}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          {current !== null ? (
            <Tooltip
              x={(current + 0.5) / days.length}
              top={`${(1 - (days[current]!.total || 0) / max) * 100}%`}
            >
              <span className="font-medium">{fmtLong.format(asDate(days[current]!.date))}</span>
              <TipRow
                value={number.format(Math.max(0, days[current]!.total - days[current]!.problems))}
                label={t("overview.daily.reached")}
                color="var(--accent)"
              />
              <TipRow
                value={number.format(days[current]!.problems)}
                label={t("overview.daily.problems")}
                color="var(--danger)"
              />
            </Tooltip>
          ) : null}
        </div>
        <div />
        <div className="mt-2 flex justify-around gap-[2px] text-[10px] text-muted" aria-hidden>
          {days.map((d, i) => (
            <span key={d.date} className="flex min-w-0 flex-1 justify-center whitespace-nowrap">
              {i % labelEvery === 0 ? fmtDay.format(asDate(d.date)) : ""}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meter: a share against 100%
// ---------------------------------------------------------------------------

export function Meter({
  value,
  label,
  tone = "accent",
}: {
  value: number;
  label: string;
  tone?: Hue | "success";
}) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    // biome-ignore lint/a11y/useSemanticElements: a native <meter> cannot be themed consistently across browsers.
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct * 100)}
      className="h-1.5 w-full overflow-hidden rounded-full"
      style={{ background: `color-mix(in oklab, var(--${tone}) 16%, var(--surface))` }}
    >
      <div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: `var(--${tone})` }} />
    </div>
  );
}
